import classNames from 'classnames';

import type { Appointment } from '../../../../react-query/useAppointments';
import type {
  TProcedureStatus,
  TVisitSummary,
} from '../../../../react-query/useVisitSummaries';
import { getPatientAge } from '../patientStripRedesigned/patientInfoSheet/PatientInfo';

import {
  isUndetectedPediatricDentition,
  useIsBasicOdontogram,
  useToothStatuses,
} from './hooks';
import { Quadrant } from './quadrant';
import { MouthHover } from './quadrantHover/mouthHover';
import type { ToothStatus } from './Tooth';
import type { TGridOrderQuadrantAbbreviations } from './types';
import { GridOrderQuadrantAbbreviations } from './types';
import {
  hasPermanentOnlyTeeth,
  hasPrimaryTeeth,
  isLowerQuadrant,
  isRightQuadrant,
} from './utils';

export const TEETH_PER_QUADRANT = 8;
export const QUADRANTS = 4;
const teethOrder = {
  UR: { start: 0, end: 8 },
  UL: { start: 8, end: 16 },
  LR: { start: 24, end: 32 },
  LL: { start: 16, end: 24 },
};

export const getQuadrantTeeth = (
  quadrant: TGridOrderQuadrantAbbreviations,
  toothStatuses: ToothStatus[],
  hidePermanentOnlyTeeth: boolean,
) => {
  const teethStart = teethOrder[quadrant].start;
  const teethEnd = teethOrder[quadrant].end;

  const displayToothStatuses = toothStatuses.slice(teethStart, teethEnd);

  if (isLowerQuadrant(quadrant)) {
    // Reverse order for the last two quadrants
    displayToothStatuses.reverse();
  }

  if (!hidePermanentOnlyTeeth) return displayToothStatuses;

  if (isRightQuadrant(quadrant)) {
    // Remove leftmost three permanent teeth
    return displayToothStatuses.slice(3);
  }
  // Remove rightmost three permanent teeth
  return displayToothStatuses.slice(0, 5);
};

export const PatientStripOdontogram = ({
  activeImageId,
  age,
  appointment, // Appointment will only be passed in the context of the patient list and not the image viewer.
  module = 'patient-list',
  scaleFactor = 1,
  selectedStatuses,
  visitSummary,
}: {
  activeImageId?: string;
  // We need to pass age as an independent prop because we don't have an
  // appointment in the context of the image viewer.
  age: number | null;
  appointment?: Appointment;
  module?: 'image-viewer' | 'patient-list';
  scaleFactor?: number;
  selectedStatuses: TProcedureStatus[];
  visitSummary?: TVisitSummary;
}) => {
  const isBasicOdontogram = useIsBasicOdontogram();
  const hasImages =
    !!appointment?.patient.imagingPatientId || module === 'image-viewer'; // Appointment will be undefined in the image viewer context.
  const patientAge = age ?? getPatientAge(appointment?.patient.dob);
  const toothStatuses = useToothStatuses(
    visitSummary,
    selectedStatuses,
    patientAge,
    hasImages,
  );

  // Show the 20-tooth odontogram if there are primary and no permanent-only
  // teeth or if the patient is <= 6 years old without any images.
  const hidePermanentOnlyTeeth =
    (hasPrimaryTeeth(visitSummary) && !hasPermanentOnlyTeeth(visitSummary)) ||
    isUndetectedPediatricDentition(age, hasImages);

  return (
    <div
      data-testid="mini-odontogram"
      className={classNames('vai-flex vai-justify-center vai-items-center ', {
        'vai-mt-3 vai-mb-2 [@media(max-height:570px)]:vai-mt-0 [@media(max-height:570px)]:vai-mb-0':
          module === 'image-viewer',
        'vai-h-40': module === 'patient-list',
        'vai-w-96': module === 'patient-list',
      })}
    >
      <div className="vai-relative vai-grid vai-grid-cols-2 vai-grid-rows-2 vai-gap-[2px] vai-text-xs">
        {!isBasicOdontogram && (
          <MouthHover
            activeImageId={activeImageId}
            appointment={appointment}
            module={module}
            visitSummary={visitSummary}
            selectedStatuses={selectedStatuses}
          />
        )}
        {(GridOrderQuadrantAbbreviations as readonly string[]).map(
          (quadrant) => {
            const quadrantToothStatuses = getQuadrantTeeth(
              quadrant as TGridOrderQuadrantAbbreviations,
              toothStatuses,
              hidePermanentOnlyTeeth,
            );

            return (
              <Quadrant
                activeImageId={activeImageId}
                appointment={appointment}
                key={quadrant}
                module={module}
                quadrant={quadrant as TGridOrderQuadrantAbbreviations}
                scaleFactor={scaleFactor}
                selectedStatuses={selectedStatuses}
                toothStatuses={quadrantToothStatuses}
                visitSummary={visitSummary}
              />
            );
          },
        )}
      </div>
    </div>
  );
};
